import { Box, Button, Tooltip } from '@mui/material'
import { Download, HowToVote, MoreVert, Preview } from '@mui/icons-material'

const AdminHelpPulse = ({ showTitle = true }) => {
  const handleDownloadTemplate = () => {
    const rows = [
      ['Key', 'Value', 'Description / Instructions'],
      ['Title', 'Daily Pulse', 'Starts a new section. Always required first.'],
      ['Subtitle', 'How is your week going?', 'Optional sub-header text.'],
      ['Type', 'SLIDER', 'Types: SLIDER, AB_TESTING, GRID, or CHOICE.'],
      [
        'Prompt',
        'I feel productive today',
        'SLIDER sections use "Prompt" rows.'
      ],
      ['Title', 'Feature Preference', 'New section starts here.'],
      ['Type', 'AB_TESTING', 'AB_TESTING sections use "Option" rows.'],
      ['Option', 'Dark Mode', ''],
      ['Option', 'Light Mode', ''],
      ['Title', 'Feedback Grid', ''],
      ['Type', 'GRID', 'GRID sections use "GridRow" and "GridCol" rows.'],
      ['GridRow', 'Ease of use', ''],
      ['GridCol', 'Poor', ''],
      ['GridCol', 'Average', ''],
      ['GridCol', 'Great', ''],
      ['Title', 'Favorite Hobbies', ''],
      ['Type', 'CHOICE', 'CHOICE sections use "Choice" rows.'],
      ['Choice', 'Coding', ''],
      ['Choice', 'Design', '']
    ]

    const csvContent = rows
      .map(e => e.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = window.document.createElement('a')
    link.href = url
    link.setAttribute('download', 'PulseSectionTemplate.csv')
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
  return (
    <>
      {showTitle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'primary.main'
          }}
        >
          <HowToVote /> <h3>Upload a Pulse</h3>
        </Box>
      )}
      <b>Pulse Information</b>
      <br />
      Required:
      <ul>
        <li>File Name,</li> <li>Organization Name</li>
      </ul>
      Optional:
      <ul>
        <li>Logo,</li> <li>Host Name,</li> <li>Description,</li>
        <li>Contact Information,</li>
        <li>Contact Link (must be a valid url)</li>
      </ul>
      <br />
      <b>Pulse Details</b>
      <br />
      Pulse Values
      <ul>
        <li>
          Add and edit pulse values; these are the responding options for users,
        </li>
        <li>
          We default "Strongly Disagree", "Disagree", "Neutral", "Argee",
          "Strongly Agree",
        </li>
        <li>You can have between two and ten Pulse values</li>
      </ul>
      Pulse Headers
      <br />
      Use Pulse Headers to create groupings; users will be able to filter
      sections by Pulse Header or see all sections at the same time,
      <ul>
        <li>
          Optionally, add Pulse headers;
          <ul>
            <li>
              <b>
                Add these first, if you will include them. Every edit will make
                you choose them again later.
              </b>
            </li>
            <li>
              Open the Pulse Headers collapsed accordion and select an icon and
              type in your pulse header,
            </li>
            <li>
              <b>Headers are optional, and not included in the CSV import.</b>
            </li>
          </ul>
        </li>
        <li>
          After adding a header, you can add sections to that grouping by
          choosing the corresponding header in the "Section Header" dropdown
          within "Pulse Sections";
          <ul>
            <li>
              With headers setup, it is required to add one to each section.
            </li>
          </ul>
        </li>
      </ul>
      Pulse Section (at least one section is required)
      <ul>
        <li>
          If headers (groupings) are setup, a header is requied for each
          section,
        </li>
        <li>A title is requied for each section,</li>
        <li>A subtitle/description is optional for each section,</li>
        <li>An image is optional for each section,</li>
        <ul>
          <li>
            To add an image click `Upload a Section Image` and view the preview,
          </li>
          <li>
            <b>
              Images are not supported through CSV import but can be added
              afterwards,
            </b>
          </li>
          <li>
            Add Alt Text for each image for accessibility and to provide context
            for users who cannot see the image,
          </li>
        </ul>
        <li>
          <b>Section Types:</b>
        </li>
        <ul>
          <li>
            <b>Slider Sections:</b>
          </li>
          <ul>
            <li>
              At least one prompt is required for each section, but add as many
              as you want by clicking "Add Prompt",
            </li>
          </ul>
          <li>
            <b>A/B Testing Sections:</b>
          </li>
          <ul>
            <li>
              At least two options are required for each section, but add as
              many as you want by clicking "Add Option",
            </li>
            <li>Each option must have unique text,</li>
            <li>Add an optional image and alt text to each option,</li>
          </ul>
          <li>
            <b>Multiple Choice Sections:</b>
          </li>
          <ul>
            <li>
              At least one choice is required for each section, but add as many
              as you want by clicking "Add Choice",
            </li>
            <li>Each choice must have unique text,</li>
          </ul>
          <li>
            <b>Multiple Choice Grid Sections:</b>
          </li>
          <ul>
            <li>
              At least one row is required for each section, but add as many as
              you want by clicking "Add Row",
            </li>
            <li>
              At least two columns are required for each section, but add as
              many as you want by clicking "Add Column",
            </li>
            <li>Each column must have unique text,</li>
          </ul>
          <li>
            When changing Section Types you must delete all text and images.
          </li>
        </ul>
        <li>Add another section by clicking "Add Section"</li>
      </ul>
      Importing CSV
      <ul>
        <li>
          Click Import CSV below the first section to browse and upload your
          formatted file,
        </li>

        <li>
          If errors are detected, they will be listed below with details on how
          to fix them. We recommend resolving the first error first, as
          subsequent issues may be resolved automatically once the initial
          formatting is corrected,
        </li>
        <li>
          <Tooltip title='Download CSV template'>
            <Button
              startIcon={<Download />}
              variant='outlined'
              sx={{ mr: 1 }}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
          </Tooltip>
          or follow the template shown below:
        </li>
      </ul>
      <table
        style={{
          border: '1px solid black',
          borderCollapse: 'collapse'
        }}
        border={1}
      >
        <tbody>
          <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
            <td>Key</td>
            <td>Value</td>
            <td>Description</td>
          </tr>
          <tr style={{ borderTop: '2px solid gray' }}>
            <td>Title</td>
            <td>Daily Pulse</td>
            <td>Starts a new section (Required)</td>
          </tr>
          <tr>
            <td>Subtitle</td>
            <td>How is your week going?</td>
            <td>Optional sub-header text</td>
          </tr>
          <tr>
            <td>Type</td>
            <td>SLIDER</td>
            <td>Interaction type: SLIDER, AB_TESTING, GRID, or CHOICE</td>
          </tr>
          <tr>
            <td>Prompt</td>
            <td>I feel productive today</td>
            <td>Add as many "Prompt" rows as you want</td>
          </tr>
          <tr style={{ borderTop: '2px solid gray' }}>
            <td>Title</td>
            <td>Feature Preference</td>
            <td>Starts next section</td>
          </tr>
          <tr>
            <td>Type</td>
            <td>AB_TESTING</td>
            <td>Requires "Option" rows below</td>
          </tr>
          <tr>
            <td>Option</td>
            <td>Dark Mode</td>
            <td>Choice A</td>
          </tr>
          <tr>
            <td>Option</td>
            <td>Light Mode</td>
            <td>Choice B</td>
          </tr>
          <tr style={{ borderTop: '2px solid gray' }}>
            <td>Title</td>
            <td>Feedback Grid</td>
            <td>Starts next section</td>
          </tr>
          <tr>
            <td>Type</td>
            <td>GRID</td>
            <td>Requires "GridRow" and "GridCol"</td>
          </tr>
          <tr>
            <td>GridRow</td>
            <td>Ease of use</td>
            <td>The question/row label</td>
          </tr>
          <tr>
            <td>GridCol</td>
            <td>Poor</td>
            <td>The answer/column header</td>
          </tr>
          <tr>
            <td>GridCol</td>
            <td>Great</td>
            <td>The answer/column header</td>
          </tr>

          <tr style={{ borderTop: '2px solid gray' }}>
            <td>Title</td>
            <td>Favorite Hobbies</td>
            <td>Starts next section</td>
          </tr>
          <tr>
            <td>Type</td>
            <td>CHOICE</td>
            <td>Requires "Choice" rows below</td>
          </tr>
          <tr>
            <td>Choice</td>
            <td>Coding</td>
            <td>Checkbox option</td>
          </tr>
          <tr>
            <td>Choice</td>
            <td>Design</td>
            <td>Checkbox option</td>
          </tr>

          <tr style={{ borderTop: '2px solid gray' }}>
            <td colSpan={3} style={{ textAlign: 'center', color: '#666' }}>
              <em>...and so on for as many sections as you need</em>
            </td>
          </tr>
        </tbody>
      </table>
      <br />
      <br />
      <b>Pulse Configuration</b>
      <br />
      Required:{' '}
      <ul>
        <li>Visible Dates (dates any user can see the document)</li>
      </ul>
      Optional:{' '}
      <ul>
        <li>
          Admin Emails (share admin permissions with other users, they must sign
          up with the inputted email),
        </li>{' '}
        <li>
          Allow Responses (dates must be between Visible Dates and{' '}
          <b>without response dates users will not be able to response</b>
          ),
        </li>
        <li>
          Comment Visibility (allow users to see all comments or only their own;
          users will still be able to see Pulse results),
        </li>
        <li>
          Custom URL (share the document with a custom url, must be unique
          across the platform),
        </li>
        <li>
          Thank You Message (a custom message users will see after responding to
          the entire Pulse),
        </li>
        <li>Access Code (add a password to the document),</li>
        <li>
          Searchable by public (allow document to be found on search tab),
        </li>
        <li>Require sign in to view</li>
      </ul>
      <br />
      <br />
      <b>Preview Mode</b>
      <br />
      <ul>
        <li>
          Click <MoreVert color='primary' /> in the top right corner and select{' '}
          <Preview /> Preview Mode.
        </li>
        <li>
          In Preview Mode, you can interact with the Pulse and see how it will
          look to users, but your responses will be saved and marked as admin
          responses,
        </li>
        <li>
          To exit Preview Mode, click the alert that describes Preview Mode.
          This will delete your responses and bring you back to admin view, but
          <b>
            {' '}
            if you navigate away or take any other actions (like deleting other
            users' responses) while in preview mode, your changes will be saved.
          </b>
        </li>
      </ul>
    </>
  )
}

export default AdminHelpPulse
